import type { Schemas } from "@/lib/api/client";
import type { ConfigStatus } from "@/lib/api/queries";

export const CONFIG_STATUS: Record<ConfigStatus, string> = {
  active: "Активен",
  suspended: "Приостановлен",
  revoked: "Отозван",
  revoked_externally: "Удалён в приложении",
};

export const CONFIG_ORIGIN: Record<Schemas["ConfigOrigin"], string> = {
  self: "Сам создал",
  admin: "Выдан админом",
  imported: "Из приложения Amnezia",
};

type Action = Schemas["AuditAction"];

export const AUDIT_ACTION: Record<Action, string> = {
  "user.registered": "Регистрация",
  "user.login_failed": "Неудачный вход",
  "user.password_changed": "Сменил пароль",
  "user.sessions_revoked": "Завершил сессии",
  "config.requested": "Попросил ещё конфиг",
  "admin.config_request_approved": "Одобрил запрос конфига",
  "admin.config_request_rejected": "Отклонил запрос конфига",
  "admin.user_role_changed": "Сменил роль",
  "admin.user_note_changed": "Изменил заметку",
  "admin.user_signed_out": "Разлогинил везде",
  "admin.user_deleted": "Удалил пользователя",
  "admin.announcement_created": "Опубликовал объявление",
  "admin.announcement_deactivated": "Снял объявление",
  "admin.created": "Создан администратор",
  "admin.user_blocked": "Заблокирован",
  "admin.user_unblocked": "Разблокирован",
  "admin.user_limit_changed": "Изменён лимит",
  "admin.user_password_reset": "Сброшен пароль",
  "config.created": "Создан конфиг",
  "admin.config_issued": "Выдан конфиг",
  "config.reissued": "Перевыпущен конфиг",
  "config.renamed": "Переименован конфиг",
  "config.deleted": "Удалён конфиг",
  "admin.config_revoked": "Отозван конфиг",
  "admin.config_assigned": "Конфиг привязан",
  "sync.config_imported": "Найден конфиг из приложения",
  "sync.config_revoked_externally": "Конфиг удалён в приложении",
  "sync.config_restored_externally": "Конфиг вернулся на сервер",
  "admin.registration_mode_changed": "Режим регистрации изменён",
  "admin.invite_created": "Создан инвайт",
  "admin.invite_deactivated": "Инвайт отключён",
  "peer.connected": "Подключился",
  "peer.disconnected": "Отключился",
  "peer.endpoint_changed": "Сменился IP во время подключения",
};

const CONNECTION: Action[] = ["peer.connected", "peer.disconnected", "peer.endpoint_changed"];

/** Filters of the log page. `null` actions = no filter. */
export const AUDIT_CATEGORIES = [
  {
    id: "activity",
    label: "Действия",
    actions: (Object.keys(AUDIT_ACTION) as Action[]).filter((a) => !CONNECTION.includes(a)),
  },
  { id: "connections", label: "Подключения", actions: CONNECTION },
  {
    id: "security",
    label: "Безопасность",
    actions: [
      "user.login_failed",
      "user.password_changed",
      "user.sessions_revoked",
      "admin.user_password_reset",
      "admin.user_blocked",
      "admin.user_unblocked",
      "admin.user_role_changed",
      "admin.user_signed_out",
      "admin.user_deleted",
      "admin.created",
      "peer.endpoint_changed",
    ],
  },
  {
    id: "configs",
    label: "Конфиги",
    actions: [
      "config.created",
      "admin.config_issued",
      "config.reissued",
      "config.renamed",
      "config.deleted",
      "admin.config_revoked",
      "admin.config_assigned",
      "sync.config_imported",
      "sync.config_revoked_externally",
      "sync.config_restored_externally",
    ],
  },
  { id: "all", label: "Всё", actions: null },
] as const satisfies readonly { id: string; label: string; actions: readonly Action[] | null }[];

export type AuditCategory = (typeof AUDIT_CATEGORIES)[number]["id"];

/** Events worth a second look get the danger colour in the log. */
export const ALARMING: ReadonlySet<Action> = new Set<Action>([
  "user.login_failed",
  "admin.user_blocked",
  "admin.config_revoked",
  "sync.config_revoked_externally",
  "peer.endpoint_changed",
]);
